import { z } from 'zod';

import { zodDatetime } from '../../utils/zodHelpers';
import { serviceBundleBasicSchema } from '../api/serviceBundles';
import { addressSchema, addressWithLatLngSchema } from './addresses';
import {
    dayOfWeekHoursSchema,
    dropReasonSchema,
    languageSchema,
    organizationBasicSchema,
    patientBasicSchema,
    qhpSimpleSchema,
    qhpTeamBasicSchema,
    serviceAreaBasicSchema,
    sexSchema,
    territorySimple,
} from './basicSchemas';
import { ShiftQualificationSchema, qualificationBasicSchema } from './qualificationSchemas';

export const disciplineSchema = z.object({
    uuid: z.string().uuid(),
    name: z.string().nullable(),
    code: z.string(),
    display_name: z.string(),
});
export type DisciplineZod = z.infer<typeof disciplineSchema>;

export const roleSchema = z.object({
    name: z.string(),
    code: z.string(),
});

export const serviceLimitSchema = z.object({
    service_uuid: z.string().nullable(),
    limit: z.number().nullable(),
});
export type ServiceLimit = z.infer<typeof serviceLimitSchema>;

export const documentWithLinkSchema = z.object({
    uuid: z.string().uuid(),
    created_at: zodDatetime(),
    doc_type_name: z.string(),
    download_link: z.string(),
});
export type DocumentWithLink = z.infer<typeof documentWithLinkSchema>;

export const qhpForListDisplayPermissionsSchema = z.object({
    edit: z.boolean(),
});
export const qhpForListDisplaySchema = qhpSimpleSchema.extend({
    // Disabling the .email() thing for now, as sadly, we have some invalid
    //  values in our production data.
    // email: z.string().email(),
    email: z.string(),
    phone: z.string(),
    // Address added to QHPBasic, can be removed here in a future release.
    address_dict: addressWithLatLngSchema.nullable(),
    organization: organizationBasicSchema,
    clinician_roles: z.array(roleSchema),
    territories: z.array(territorySimple),
    disciplines: z.optional(z.array(disciplineSchema).nullable()),
    virtual_territories: z.array(territorySimple),
    qualifications: z.array(qualificationBasicSchema),
    permissions: qhpForListDisplayPermissionsSchema,
});
export type QhpForListDisplay = z.infer<typeof qhpForListDisplaySchema>;

export const shiftSchema = z.object({
    uuid: z.string(),
    shift_rule_id: z.string().uuid(),
    notes: z.string(),
    timezone: z.string(),
    recurrence_rule: z.string(),
    drop_reason: dropReasonSchema.nullable(),
    visit_count: z.number(),
    distance_traveled: z.number(),
    professional: qhpSimpleSchema,
    start_datetime: zodDatetime(),
    end_datetime: zodDatetime(),
    is_complete: z.boolean(),
    cancellation_options: z.array(dropReasonSchema),
    qualification_restrictions: z.array(ShiftQualificationSchema),
    organization: organizationBasicSchema,
    always_track_location: z.boolean(),
    // Where the shift is happening
    territories: z.array(territorySimple),
    virtual_territories: z.array(territorySimple),
    clinic: serviceAreaBasicSchema.nullable(),
    start_address: addressSchema.nullable(),
    end_address: addressSchema.nullable(),
    service_limits: z.array(serviceLimitSchema).nullable(),
});
export type Shift = z.infer<typeof shiftSchema>;

export const shiftBlockSchema = z.object({
    uuid: z.string(),
    start_datetime: zodDatetime(),
    end_datetime: zodDatetime(),
    qhp: qhpSimpleSchema,
    clinicians: z.array(qhpSimpleSchema),
    clinician_teams: z.array(qhpTeamBasicSchema),
    address: addressWithLatLngSchema.nullable(),
    timezone: z.string(),
    notes: z.string(),
    rrule_str: z.string(),
});

export type ShiftBlock = z.infer<typeof shiftBlockSchema>;

export const patientForListDisplaySchema = patientBasicSchema.extend({
    organization: organizationBasicSchema,
    external_id: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    sex: sexSchema,
    preferred_language: languageSchema,
});
export type PatientForListDisplay = z.infer<typeof patientForListDisplaySchema>;

export const serviceAreaForListDisplaySchema = z.object({
    uuid: z.string().uuid(),
    name: z.string(),
    organization: organizationBasicSchema,
    territories: z.array(territorySimple),
    service_bundles: z.array(serviceBundleBasicSchema),
    clinic_address: addressWithLatLngSchema.nullable(),
});

export type ServiceAreaForListDisplay = z.infer<typeof serviceAreaForListDisplaySchema>;

export const serviceAreaSchema = z.object({
    // Basic info -- maybe we should actually extend ServiceAreaBasic???
    uuid: z.string().uuid(),
    name: z.string(),
    organization: organizationBasicSchema,

    // Service area info.
    timezone: z.string(),

    lead_time: z.number(),

    territories: z.array(territorySimple),
    service_bundles: z.array(serviceBundleBasicSchema),
    clinic_address: addressWithLatLngSchema.nullable(),
    operating_hours: z.array(dayOfWeekHoursSchema),
    operating_timeslots: z.array(dayOfWeekHoursSchema),
    // Routing Settings
    padding: z.number(),
    radius: z.number(),
    use_dynamic_reachability: z.boolean(),
    max_continuous_travel_duration: z.number(),
});

export const contactSchema = z
    .object({
        first_name: z.string(),
        last_name: z.string(),
        email: z.string().nullable(),
        phone: z.string().nullable(),
    })
    .nullable();

export const clinicianAvailabilityDataSchema = z.object({
    start_time: zodDatetime(),
    density_graph_value: z.number(),
    healthbar_value: z.number(),
});

export type clinicianAvailabilityData = z.infer<typeof clinicianAvailabilityDataSchema>;
